// @ts-nocheck
import { sanitizeCoordinatorFilter } from "../pages/dashboard/_components/dashboardCoordinators";
import {
  isDefaultEventTypes,
  sanitizeEventTypes,
} from "../pages/dashboard/_components/eventTypes";
import { sanitizeQuickView } from "../pages/dashboard/_components/quickViews";
import { financePreferences } from "../pages/dashboard/finance/financialDashboard.utils";
/**
 * Dashboard Filters Utility Functions
 * Provides helper functions for managing dashboard filter state
 */

/**
 * Extract filter data from Dashboard state
 * @param {object} filterParams - Current filter parameters
 * @param {object} filterDates - Current filter dates
 * @param {array} searchBy - Current search filters
 * @param {object} coordinatorFilter - { selected: [{_id, fullName, ...}], match: "any"|"all" }
 * @param {array} eventTypes - "Show:" type chips (drafts / new requests / estimates sent / approved)
 * @param {object} quickView - Quick view chip ({ view, days } or null)
 * @param {number} rowsPerPage - Rows per page
 * @param {object} hiddenColumns - Hidden column visibility state
 * @returns {object} Structured filters object
 */
export const extractFiltersFromState = ({
  filterParams,
  filterDates,
  searchBy,
  coordinatorFilter,
  eventTypes,
  quickView,
  showDuplicates,
  finance,
  rowsPerPage,
  hiddenColumns,
}: Record<string, any>) => {
  const selectedCoordinators = coordinatorFilter?.selected || [];
  return {
    search: filterParams?.search || "",
    sortOrder: filterParams?.sortOrder || "",
    sortField: filterParams?.sortField || "",
    dateRange: {
      startDate: filterDates?.startDate || "",
      endDate: filterDates?.endDate || "",
    },
    searchFilters: searchBy?.map((item) => ({
      value: item?.value,
      subSort: item?.subSort,
      label: item?.label,
    })) || [],
    // Only present when active, so filters saved before this key existed
    // still compare equal to an empty coordinator filter.
    ...(selectedCoordinators.length
      ? {
          coordinatorFilter: {
            selected: selectedCoordinators.map((c) => ({
              _id: c?._id,
              fullName: c?.fullName,
              email: c?.email,
              profileImage: c?.profileImage,
            })),
            match: coordinatorFilter?.match === "all" ? "all" : "any",
          },
        }
      : {}),
    // "Show:" type chips — only when not the default, same reason as above.
    ...(eventTypes && !isDefaultEventTypes(eventTypes)
      ? { eventTypes: sanitizeEventTypes(eventTypes) }
      : {}),
    // Quick view chip — only when one is active, same reason as above.
    ...(sanitizeQuickView(quickView) ? { quickView: sanitizeQuickView(quickView) } : {}),
    ...(showDuplicates === true ? { showDuplicates: true } : {}),
    ...(financePreferences(finance).status !== "all" || financePreferences(finance).archivedOnly
      ? { finance: financePreferences(finance) } : {}),
    rowsPerPage: rowsPerPage || 10,
    ...hiddenColumns ? { hiddenColumns: hiddenColumns } : {}, // Include hidden columns
  };
};

/**
 * Apply saved filters to the Dashboard state
 * @param {object} savedFilters - Saved filters from backend
 * @returns {object} Object with filter state updates and hidden columns
 */
export const applyFiltersToState = (savedFilters) => {
  if (!savedFilters?.filters) {
    return null;
  }

  const { filters } = savedFilters;
  return {
    filterParams: {
      search: filters?.search || "",
      sortOrder: filters?.sortOrder || "",
      sortField: filters?.sortField || "",
    },
    filterDates: {
      startDate: filters?.dateRange?.startDate || "",
      endDate: filters?.dateRange?.endDate || "",
    },
    searchBy: filters?.searchFilters || [],
    // Saved filters are free JSON from the API: keep only well-formed entries
    // (24-hex ids, string fields) so a corrupted document cannot break the page.
    ...(filters?.coordinatorFilter?.selected?.length
      ? (() => {
          const clean = sanitizeCoordinatorFilter(filters.coordinatorFilter);
          return clean.selected.length ? { coordinatorFilter: clean } : {};
        })()
      : {}),
    ...(Array.isArray(filters?.eventTypes) && filters.eventTypes.length
      ? { eventTypes: sanitizeEventTypes(filters.eventTypes) }
      : {}),
    ...(sanitizeQuickView(filters?.quickView) ? { quickView: sanitizeQuickView(filters.quickView) } : {}),
    showDuplicates: filters?.showDuplicates === true,
    finance: financePreferences(filters?.finance),
    rowsPerPage: filters?.rowsPerPage || 10,
    ...filters?.hiddenColumns ? { hiddenColumns: filters?.hiddenColumns } : {}, // Restore hidden columns
  };
};

/**
 * Check if current filters are different from saved filters
 * @param {object} currentFilters - Current filter state
 * @param {object} savedFilters - Saved filters from backend
 * @returns {boolean} True if filters have changed
 */
export const hasFiltersChanged = (currentFilters={}, savedFilters={}) => {
  if (!savedFilters?.filters) {
    return Object.values(currentFilters).some(
      (val) => val !== "" && val !== null && val !== undefined
    );
  }

  return JSON.stringify(currentFilters) !==
    JSON.stringify(savedFilters?.filters)
    ? true
    : false;
};

export const hasAnyValues = (obj) => {
  return Object.values(obj).some(
    (val) => val !== "" && val !== null && val !== undefined
  );
}

export const compareObjects = (obj1, obj2) => {
  return JSON.stringify(obj1) === JSON.stringify(obj2);
};

/**
 * Create a debounced version of filter save
 * Useful for auto-saving filters as user makes changes
 * @param {function} saveFunction - The save function to debounce
 * @param {number} delay - Delay in milliseconds (default: 2000)
 * @returns {function} Debounced function
 */
export const createDebouncedFilterSave = (saveFunction, delay = 2000) => {
  let timeoutId = null;

  return (...args) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      saveFunction(...args);
      timeoutId = null;
    }, delay);
  };
};
